import { parseDateOnly, startOfDay } from './assigned-queue';
import { daysBetween, formatCalendarWeek, startOfLocalWeek } from './dates';

type Finished = { completed_at: string | null; due_date: string | null };

/** "This week", "Last week", then the Monday–Sunday range; items with no finish date go last. */
export function groupByFinishWeek<T extends Finished>(
  items: T[],
  today: Date,
): Array<{ id: string; title: string; items: T[] }> {
  const thisMonday = startOfLocalWeek(today).getTime();
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(lastMonday.getDate() - 7);
  const groups = new Map<string, { id: string; title: string; items: T[] }>();
  for (const item of items) {
    const finished = item.completed_at ? new Date(item.completed_at) : null;
    let id = 'unknown';
    let title = 'Finish date not recorded';
    if (finished && !Number.isNaN(finished.getTime())) {
      const monday = startOfLocalWeek(finished).getTime();
      id = String(monday);
      title =
        monday === thisMonday ? 'This week' : monday === lastMonday.getTime() ? 'Last week' : formatCalendarWeek(finished);
    }
    const group = groups.get(id) ?? { id, title, items: [] };
    group.items.push(item);
    groups.set(id, group);
  }
  const unknown = groups.get('unknown');
  groups.delete('unknown');
  return [...Array.from(groups.values()), ...(unknown ? [unknown] : [])];
}

/** Whether it finished by its due date: "On time", "3 days late", or null with no due date. */
export function finishedOnTime(item: Finished): { late: boolean; text: string } | null {
  if (!item.due_date || !item.completed_at) return null;
  const due = parseDateOnly(item.due_date);
  const finished = new Date(item.completed_at);
  if (!due || Number.isNaN(finished.getTime())) return null;
  const days = daysBetween(due, startOfDay(finished));
  if (days <= 0) return { late: false, text: 'On time' };
  return { late: true, text: `${days} ${days === 1 ? 'day' : 'days'} late` };
}
